import {
  diffPaypalHKOrderToken,
  diffPaypalHKPayAction,
  getPaypalHKOrderToken,
  paypalHKPayAction,
} from '@/api/pay';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { env } from '@/utils/env';
import { DEFAULT_PAYPAL_OPTIONS } from '@/providers/paypal-global-script-provider';
import {
  PayPalButtons,
  usePayPalScriptReducer,
  DISPATCH_ACTION,
  SCRIPT_LOADING_STATE,
} from '@paypal/react-paypal-js';
import { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';

// 定义明确的类型
type OrderSource = 'payment' | 'order' | 'priceDiff';

interface OrderData {
  order_id?: string;
  id?: string;
  purchase_currency?: string;
  [key: string]: any;
}

interface PayPalProps {
  /**
   * 订单详情数据
   */
  order?: OrderData;
  /**
   * 创建订单函数
   */
  createOrder?: (paymentMethod?: string) => Promise<OrderData>;
  /**
   * 来源
   */
  from?: OrderSource;
  /**
   * 高度
   */
  height?: number;
  /**
   * 渲染PayPal按钮的目标元素id（CSS选择器），如#paypal-btn
   */
  domId?: string;
  /**
   * 支付成功事件
   */
  onSuccess?: (order?: OrderData) => void;
  /**
   * 支付失败事件
   */
  onError?: (msg?: string, order?: OrderData) => void;
  /**
   * 初始化事件
   */
  onPaypalInit?: () => void;
  /**
   * 点击事件
   */
  onPaypalOnClick?: () => void;

  /**
   * 是否显示PayPal按钮
   */
  children?: React.ReactNode;

  /**
   * domRef 为什么增加这个参数，为了兼容渲染时顺的问题，在某些情况下 在初始化paypal组件的时候 domid 所在的元素还未渲染出来，所以会导致挂在不上的情况。
   */
  domRef?: React.RefObject<HTMLDivElement>;
}

/**
 * PayPal支付组件
 * 处理PayPal支付流程
 *
 * @CAUTION
 *  - 为了加载性能，没有在 RootLayout 里添加 PaypalGlobalScriptProvider
 *  - 因此需要在子 Layout 里添加 PaypalGlobalScriptProvider
 */
export default function PayPal({
  order = {},
  createOrder = () => Promise.resolve({}),
  from = 'payment',
  height = 32,
  onSuccess,
  onError,
  onPaypalInit,
  onPaypalOnClick,
  domId,
  children,
  domRef,
}: PayPalProps) {
  const [selfOrder, setSelfOrder] = useState<OrderData>({});
  // 收集实际容器，避免首次渲染时获取不到 DOM
  const [containerEl, setContainerEl] = useState<HTMLElement | null>(null);
  const [{ isInitial, isRejected, options }, dispatch] = usePayPalScriptReducer();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (domId) {
      setContainerEl(document.querySelector(domId) as HTMLElement | null);
    } else if (domRef?.current) {
      setContainerEl(domRef.current);
    }
  }, [domId, domRef]);

  // 从 store 中获取币种信息
  const currency = useStore((state) => state.currency);

  const targetCurrency = useMemo(() => {
    const result = from === 'order' ? order?.currency?.name : currency?.name;
    return result || currency?.name || env.NEXT_PUBLIC_DEFAULT_CURRENCY;
  }, [from, order?.currency?.name, currency?.name]);

  // 更新PayPal配置选项
  useEffect(() => {
    // 货币相同，则不需要更新
    if (targetCurrency === options.currency) return;
    // 更新PayPal配置选项
    dispatch({
      type: DISPATCH_ACTION.RESET_OPTIONS,
      value: {
        ...DEFAULT_PAYPAL_OPTIONS,
        currency: targetCurrency,
      },
    });
  }, [targetCurrency]);

  // 加载Paypal脚本
  useEffect(() => {
    if (isInitial || isRejected) {
      dispatch({
        type: DISPATCH_ACTION.LOADING_STATUS,
        value: SCRIPT_LOADING_STATE.PENDING,
      });
    }
  }, []);

  // 处理创建订单令牌逻辑
  const createOrderToken = async (params: any, isPriceDiff: boolean): Promise<string> => {
    const { data, code }: any = await (isPriceDiff
      ? diffPaypalHKOrderToken(params)
      : getPaypalHKOrderToken(params));
    if (code === 200) {
      return data.token;
    } else {
      handleError();
    }
  };

  // 创建订单 - 统一处理不同类型
  const handleCreateOrder = async (): Promise<string> => {
    const params: any = {};

    try {
      // 根据订单来源处理不同逻辑
      // 在组件内部创建的订单，需要将order的结果通过成功或者失败的回调 回传给父组件
      if (from === 'payment') {
        const orderData = await createOrder();
        logger.log('orderData', orderData);
        params.order_id = orderData.order_id;
        orderData.id = orderData.order_id;
        setSelfOrder(orderData);
      } else if (from === 'order') {
        params.order_id = order.id;
      } else if (from === 'priceDiff') {
        const orderData = await createOrder('paypal');
        params.diff_order_id = orderData.id;
        setSelfOrder(orderData);
        return createOrderToken(params, true);
      }

      // 非差价订单创建支付令牌
      return createOrderToken(params, false);
    } catch (error) {
      logger.error(error);
      handleError();
      return '';
    }
  };

  // 处理支付批准 - 统一处理不同类型订单
  const handleApprove = async (data: { orderID: string; [key: string]: any }): Promise<void> => {
    try {
      const isPriceDiff = from === 'priceDiff';
      const params = {
        token: data.orderID,
        store_id: 1,
        ...(isPriceDiff
          ? { diff_order_id: selfOrder.id }
          : { order_id: from === 'order' ? order.id : selfOrder.id }),
      };
      const payAction = isPriceDiff ? diffPaypalHKPayAction : paypalHKPayAction;
      const { code, msg }: any = await payAction(params);

      if (code === 200) {
        handleSuccess();
      } else {
        setTimeout(() => {
          handleError(msg);
        }, 2700);
      }
    } catch (error) {
      logger.error(error);
      handleError();
    }
  };

  // 处理点击按钮事件
  const handleClick = async (_data: any, actions: any) => {
    onPaypalOnClick?.();
    return actions.resolve();
  };

  const handleCancel = () => {
    handleError();
  };

  const handleError = (msg?: string) => {
    // onError 统一处理，方便回传
    onError?.(msg || '', selfOrder);
  };

  const handleSuccess = () => {
    // onSuccess 统一处理，方便回传
    onSuccess?.(selfOrder);
  };

  // 组件初始化
  useEffect(() => {
    onPaypalInit?.();
  }, [onPaypalInit]);

  return (
    <>
      {(() => {
        const buttonsElement = (
          <PayPalButtons
            style={{
              layout: 'horizontal',
              height: height || undefined,
              tagline: false,
            }}
            createOrder={handleCreateOrder}
            onApprove={handleApprove}
            onClick={handleClick}
            onCancel={handleCancel}
            onError={() => handleError()}
            onInit={onPaypalInit}
          />
        );

        if (containerEl) {
          return ReactDOM.createPortal(buttonsElement, containerEl);
        }
        return buttonsElement;
      })()}
      {children}
    </>
  );
}
